import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import HSEAssessmentList from './hse/HSEAssessmentList';
import HSEAssessmentForm from './hse/HSEAssessmentForm';
import HSEAssessmentViewer from './hse/HSEAssessmentViewer';

const HSEAssessmentTab = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('list'); // 'list', 'create', 'edit', 'view'
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    building_id: '',
    property_name: '',
    my_assessments: false
  });
  const [statistics, setStatistics] = useState(null);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);

  // Multi-step wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [stepValidation, setStepValidation] = useState({
    step1: false,
    step2: false
  });
  const [assessmentRequirements, setAssessmentRequirements] = useState({
    legal_wall_charts: false,
    signage_displayed: false,
    electrical_db: false,
    fire_fighting_equipment: false,
    fire_detection_systems: false,
    stacking_articles: false,
    automatic_sprinkler: false,
    evacuation_routes: false,
    housekeeping: false,
    emergency_preparedness: false,
    hazardous_chemicals: false,
    general_condition: false
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  // New state for individual field images
  const [fieldImages, setFieldImages] = useState({});

  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get API configuration from localStorage
  const getApiConfig = () => {
    try {
      const token = localStorage.getItem('properties_api_token') || '';
      const endpoint = localStorage.getItem('properties_api_endpoint') || '';
      return { token, endpoint };
    } catch (error) {
      console.error('Error getting API config:', error);
      return { token: '', endpoint: '' };
    }
  };

  // Get authentication headers
  const getAuthHeaders = () => {
    const { token, endpoint } = getApiConfig();
    const headers = {};

    if (token) {
      headers['X-Properties-API-Token'] = token;
    }
    if (endpoint) {
      headers['X-Properties-API-Endpoint'] = endpoint;
    }

    return headers;
  };

  // Create axios instance with API token headers
  const createApiRequest = () => {
    const { token, endpoint } = getApiConfig();
    const headers = {};

    if (token) {
      headers['X-Properties-API-Token'] = token;
    }
    if (endpoint) {
      headers['X-Properties-API-Endpoint'] = endpoint;
    }

    return axios.create({
      baseURL: BACKEND_URL,
      headers,
      withCredentials: true
    });
  };

  // Helper function to extract error messages from API responses
  const extractErrorMessage = (errorData, fallbackMessage = 'An error occurred') => {
    if (!errorData.detail) {
      return fallbackMessage;
    }

    // If detail is a string, return it directly
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }

    // If detail is an object with a msg property (validation error)
    if (typeof errorData.detail === 'object' && errorData.detail.msg) {
      return errorData.detail.msg;
    }

    // If detail is an array of validation errors
    if (Array.isArray(errorData.detail)) {
      return errorData.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }

    // Fallback to stringifying the object
    if (typeof errorData.detail === 'object') {
      return JSON.stringify(errorData.detail);
    }

    return fallbackMessage;
  };

  // PDF Export function
  const handleDownloadPDF = async () => {
    if (!selectedAssessment?.id) {
      toast.error('No assessment selected for export');
      return;
    }

    try {
      setIsGeneratingPDF(true);

      // Call the backend PDF export endpoint using authenticated client
      const apiClient = createApiRequest();
      const response = await apiClient.get(
        `/api/pdf/hse-assessment/${selectedAssessment.id}`,
        {
          responseType: 'blob'
        }
      );

      // Create a blob URL and download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = `HSE_Assessment_${selectedAssessment.building_name || selectedAssessment.property_name || selectedAssessment.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(url);

      toast.success('HSE Assessment PDF exported successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);

      let errorMessage = 'Failed to export PDF. Please try again.';

      // Handle specific error cases
      if (error.response?.status === 404) {
        errorMessage = 'Assessment not found or PDF generation failed.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to export this assessment.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while generating PDF. Please try again later.';
      }

      toast.error(errorMessage);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Assessment form state
  const [assessmentForm, setAssessmentForm] = useState({
    building_id: '',
    property_name: '',
    tenant_name: '',
    auditor_name: user?.name + ' ' + user?.surname || '',
    assessment_date: new Date().toISOString().split('T')[0],
    employee_count: '',
    tenant_representative: '',

    // Legal Wall Charts
    ohs_act_compliance: 'not_applicable',
    ohs_act_comments: '',
    labour_relations_act_compliance: 'not_applicable',
    labour_relations_act_comments: '',
    basic_conditions_employment_compliance: 'not_applicable',
    basic_conditions_employment_comments: '',
    coida_compliance: 'not_applicable',
    coida_comments: '',

    // Fire Fighting Equipment - Detailed Fields
    fire_extinguishers_compliance: 'not_applicable',
    fire_extinguishers_comments: '',
    fire_hose_reels_compliance: 'not_applicable',
    fire_hose_reels_comments: '',
    fire_blankets_compliance: 'not_applicable',
    fire_blankets_comments: '',
    emergency_lighting_compliance: 'not_applicable',
    emergency_lighting_comments: '',
    fire_pump_compliance: 'not_applicable',
    fire_pump_comments: '',
    fire_hydrants_compliance: 'not_applicable',
    fire_hydrants_comments: '',
    fire_suppression_system_compliance: 'not_applicable',
    fire_suppression_system_comments: '',
    fire_safety_signage_compliance: 'not_applicable',
    fire_safety_signage_comments: '',

    // Automatic Sprinkler System - Updated Fields
    sprinkler_system_serviced_compliance: 'not_applicable',
    sprinkler_system_serviced_comments: '',
    sprinkler_valves_locked_chain_padlock_compliance: 'not_applicable',
    sprinkler_valves_locked_chain_padlock_comments: '',
    sprinkler_valve_chamber_spare_parts_compliance: 'not_applicable',
    sprinkler_valve_chamber_spare_parts_comments: '',
    sprinkler_valves_labelled_compliance: 'not_applicable',
    sprinkler_valves_labelled_comments: '',
    sprinkler_valve_chamber_schematic_compliance: 'not_applicable',
    sprinkler_valve_chamber_schematic_comments: '',

    // Emergency Preparedness - Detailed Fields
    emergency_assembly_points_compliance: 'not_applicable',
    emergency_assembly_points_comments: '',
    emergency_contact_list_compliance: 'not_applicable',
    emergency_contact_list_comments: '',
    emergency_procedures_compliance: 'not_applicable',
    emergency_procedures_comments: '',
    first_aid_kits_compliance: 'not_applicable',
    first_aid_kits_comments: '',
    emergency_communication_compliance: 'not_applicable',
    emergency_communication_comments: '',
    emergency_drills_compliance: 'not_applicable',
    emergency_drills_comments: '',
    emergency_exits_compliance: 'not_applicable',
    emergency_exits_comments: '',
    emergency_wardens_compliance: 'not_applicable',
    emergency_wardens_comments: '',
    backup_power_compliance: 'not_applicable',
    backup_power_comments: '',

    // Evacuation Routes & Exits - Standalone Fields
    escape_doors_fire_exit_signage_compliance: 'not_applicable',
    escape_doors_fire_exit_signage_comments: '',
    fire_escape_plans_displayed_compliance: 'not_applicable',
    fire_escape_plans_displayed_comments: '',
    fire_detection_serviced_tested_compliance: 'not_applicable',
    fire_detection_serviced_tested_comments: '',
    fire_detection_operating_procedures_compliance: 'not_applicable',
    fire_detection_operating_procedures_comments: '',
    fire_alarm_call_points_signage_compliance: 'not_applicable',
    fire_alarm_call_points_signage_comments: '',
    dust_covers_removed_compliance: 'not_applicable',
    dust_covers_removed_comments: '',

    // Electrical Safety - Detailed Fields
    electrical_installations_compliance: 'not_applicable',
    electrical_installations_comments: '',
    electrical_testing_compliance: 'not_applicable',
    electrical_testing_comments: '',
    electrical_safety_switches_compliance: 'not_applicable',
    electrical_safety_switches_comments: '',

    // Hazardous Materials - Detailed Fields
    chemical_storage_compliance: 'not_applicable',
    chemical_storage_comments: '',
    chemical_handling_procedures_compliance: 'not_applicable',
    chemical_handling_procedures_comments: '',

    // Stacking of Articles - Detailed Field
    stacking_height_limits_compliance: 'not_applicable',
    stacking_height_limits_comments: '',

    // Housekeeping
    housekeeping_compliance: 'not_applicable',
    housekeeping_comments: '',

    // General Condition - Detailed Fields
    floors_compliance: 'not_applicable',
    floors_comments: '',
    walls_compliance: 'not_applicable',
    walls_comments: '',
    ceilings_compliance: 'not_applicable',
    ceilings_comments: '',
    windows_compliance: 'not_applicable',
    windows_comments: '',
    doors_compliance: 'not_applicable',
    doors_comments: '',
    stairways_compliance: 'not_applicable',
    stairways_comments: '',
    lighting_compliance: 'not_applicable',
    lighting_comments: '',

    // Fire Detection Systems - Standalone Fields
    fire_detection_serviced_tested_standalone_compliance: 'not_applicable',
    fire_detection_serviced_tested_standalone_comments: '',
    fire_detection_operating_procedures_standalone_compliance: 'not_applicable',
    fire_detection_operating_procedures_standalone_comments: '',
    fire_alarm_call_points_signage_standalone_compliance: 'not_applicable',
    fire_alarm_call_points_signage_standalone_comments: '',
    dust_covers_removed_standalone_compliance: 'not_applicable',
    dust_covers_removed_standalone_comments: '',

    // Signage Displayed
    signage_displayed_compliance: 'not_applicable',
    signage_displayed_comments: '',

    // Overall Assessment
    recommendations: '',
    follow_up_required: false,
    follow_up_date: null
  });

  useEffect(() => {
    loadAssessments();
    fetchProperties();
    fetchUsers();
    if (user?.role?.value === 'Admin' || user?.role?.value === 'Property Manager') {
      loadStatistics();
    }
  }, [filters]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch properties from backend
  const fetchProperties = async () => {
    try {
      const apiClient = createApiRequest();
      const response = await apiClient.get('/api/properties/');
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setProperties([]);
    }
  };

  // Fetch users from backend
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/auth/users`, {
        withCredentials: true
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    }
  };

  const loadAssessments = async () => {
    setIsLoading(true);
    setError(''); // Clear any previous errors
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);

      // Handle property name filtering by converting to building_id
      let buildingIdForFilter = filters.building_id;
      if (filters.property_name && !filters.building_id) {
        const matchingProperty = properties.find(p =>
          (p.building_name && p.building_name.toLowerCase().includes(filters.property_name.toLowerCase())) ||
          (p.buildingName && p.buildingName.toLowerCase().includes(filters.property_name.toLowerCase()))
        );
        if (matchingProperty) {
          buildingIdForFilter = matchingProperty.id;
        }
      }

      if (buildingIdForFilter) params.append('building_id', buildingIdForFilter);
      if (filters.my_assessments) params.append('my_assessments', 'true');

      const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        // Apply client-side property name filtering for better search experience
        let filteredData = data;
        if (filters.property_name) {
          filteredData = data.filter(assessment => {
            const property = properties.find(p => p.id === assessment.building_id);
            const propertyName = property?.building_name || property?.buildingName || assessment.building_id || '';
            return propertyName.toLowerCase().includes(filters.property_name.toLowerCase());
          });
        }

        setAssessments(filteredData);
      } else {
        // Only show error for actual server errors (not empty results)
        if (response.status >= 500) {
          setError('Failed to load assessments');
        } else {
          // For other status codes (like 404), just set empty array
          setAssessments([]);
        }
      }
    } catch (err) {
      setError('Error loading assessments: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments/statistics`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const handleFormChange = (field, value) => {
    setAssessmentForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Mirror the manually-entered property/location into property_name
    if (field === 'building_id') {
      setAssessmentForm(prev => ({ ...prev, property_name: value }));
    }
  };

  // Step navigation functions
  const nextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      setStepValidation(prev => ({ ...prev, step1: true }));
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateStep1 = () => {
    return (
      assessmentForm.building_id &&
      assessmentForm.tenant_name &&
      assessmentForm.auditor_name &&
      assessmentForm.assessment_date
    );
  };

  const validateStep2 = () => {
    // Check if at least one assessment requirement checkbox is selected
    const hasSelectedRequirements = Object.values(assessmentRequirements).some(value => value === true);

    // If no assessment areas are selected, validation fails
    if (!hasSelectedRequirements) {
      return false;
    }

    // If assessment requirements are selected, validation passes
    // This allows saving even when all compliance fields remain at default 'not_applicable'
    return true;
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return validateStep1();
      case 2:
        return validateStep2();
      default:
        return false;
    }
  };

  const submitAssessment = async () => {
    if (!assessmentForm.building_id || !assessmentForm.tenant_name) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const url = selectedAssessment
        ? `${BACKEND_URL}/api/health-safety/hse-assessments/${selectedAssessment.id}`
        : `${BACKEND_URL}/api/health-safety/hse-assessments`;

      const method = selectedAssessment ? 'PUT' : 'POST';

      // Combine all images: general images + individual field images
      const allImages = [
        ...uploadedImages.map(img => ({
          ...img,
          field: 'general' // Mark general images
        })),
        // Add all field-specific images
        ...Object.entries(fieldImages).flatMap(([fieldKey, images]) =>
          images.map(img => ({
            ...img,
            field: fieldKey.replace('_images', '') // Remove '_images' suffix to get field name
          }))
        )
      ];

      // Prepare assessment data with proper null handling for follow_up_date
      const assessmentData = {
        ...assessmentForm,
        employee_count: assessmentForm.employee_count === '' ? null : assessmentForm.employee_count,
        follow_up_date: (!assessmentForm.follow_up_required || !assessmentForm.follow_up_date)
          ? null
          : assessmentForm.follow_up_date,
        images: allImages.map(img => img.data || img), // Include base64 image data
        field_images: fieldImages, // Include structured field images for backend processing
        assessment_requirements: assessmentRequirements // Include assessment requirements checkboxes
      };

      const headers = {
        'Content-Type': 'application/json',
        credentials: 'include'
      };

      const requestBody = JSON.stringify(assessmentData);

      const response = await fetch(url, {
        method,
        headers,
        credentials: 'include',
        body: requestBody
      });

      if (response.ok) {
        setSuccess(selectedAssessment ? 'Assessment updated successfully' : 'Assessment created successfully');
        setActiveView('list');
        loadAssessments();
        resetForm();
      } else {
        const errorData = await response.json();
        setError(extractErrorMessage(errorData, 'Failed to save assessment'));
      }
    } catch (err) {
      setError('Error saving assessment: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAssessment = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments/${assessmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Assessment deleted successfully');
        loadAssessments();
      } else {
        const errorData = await response.json();
        setError(extractErrorMessage(errorData, 'Failed to delete assessment'));
      }
    } catch (err) {
      setError('Error deleting assessment: ' + err.message);
    }
  };

  const submitAssessmentForReview = async (assessmentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments/${assessmentId}/submit`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Assessment submitted for review');
        loadAssessments();
      } else {
        const errorData = await response.json();
        setError(extractErrorMessage(errorData, 'Failed to submit assessment'));
      }
    } catch (err) {
      setError('Error submitting assessment: ' + err.message);
    }
  };

  const approveAssessment = async (assessmentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments/${assessmentId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Assessment approved');
        loadAssessments();
      } else {
        const errorData = await response.json();
        setError(extractErrorMessage(errorData, 'Failed to approve assessment'));
      }
    } catch (err) {
      setError('Error approving assessment: ' + err.message);
    }
  };

  const rejectAssessment = async (assessmentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/hse-assessments/${assessmentId}/reject`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Assessment rejected');
        loadAssessments();
      } else {
        const errorData = await response.json();
        setError(extractErrorMessage(errorData, 'Failed to reject assessment'));
      }
    } catch (err) {
      setError('Error rejecting assessment: ' + err.message);
    }
  };

  // Image gallery handlers
  const handleImageUpload = async (category, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      const response = await axios.post(
        `${BACKEND_URL}/api/health-safety/hse-assessments/${selectedAssessment.id}/categorized-images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...getAuthHeaders()
          }
        }
      );

      if (response.data.success) {
        // Refresh the assessment data to get updated images
        await fetchHSEAssessments();
        setSelectedAssessment(prev => ({
          ...prev,
          categorized_images: response.data.categorized_images || prev.categorized_images,
          images: response.data.images || prev.images
        }));
        return { success: true };
      }
      return { success: false, error: response.data.message || 'Upload failed' };
    } catch (error) {
      console.error('Image upload error:', error);
      return { success: false, error: extractErrorMessage(error) };
    }
  };

  const handleImageDelete = async (imageId, category) => {
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/health-safety/hse-assessments/${selectedAssessment.id}/categorized-images/${imageId}`,
        {
          headers: getAuthHeaders()
        }
      );

      if (response.data.success) {
        // Refresh the assessment data to get updated images
        await fetchHSEAssessments();
        setSelectedAssessment(prev => ({
          ...prev,
          categorized_images: response.data.categorized_images || prev.categorized_images,
          images: response.data.images || prev.images
        }));
        return { success: true };
      }
      return { success: false, error: response.data.message || 'Delete failed' };
    } catch (error) {
      console.error('Image delete error:', error);
      return { success: false, error: extractErrorMessage(error) };
    }
  };

  const resetForm = () => {
    setAssessmentForm({
      building_id: '',
      property_name: '',
      tenant_name: '',
      auditor_name: user?.name + ' ' + user?.surname || '',
      assessment_date: new Date().toISOString().split('T')[0],
      employee_count: '',
      tenant_representative: '',

      // Reset all compliance fields to not_applicable
      ohs_act_compliance: 'not_applicable',
      ohs_act_comments: '',
      labour_relations_act_compliance: 'not_applicable',
      labour_relations_act_comments: '',
      basic_conditions_employment_compliance: 'not_applicable',
      basic_conditions_employment_comments: '',
      coida_compliance: 'not_applicable',
      coida_comments: '',
      fire_equipment_compliance: 'not_applicable',
      fire_equipment_comments: '',
      sprinkler_system_compliance: 'not_applicable',
      sprinkler_system_comments: '',

      // Fire Fighting Equipment - Detailed Fields
      fire_extinguishers_compliance: 'not_applicable',
      fire_extinguishers_comments: '',
      fire_hose_reels_compliance: 'not_applicable',
      fire_hose_reels_comments: '',
      fire_blankets_compliance: 'not_applicable',
      fire_blankets_comments: '',
      emergency_lighting_compliance: 'not_applicable',
      emergency_lighting_comments: '',
      fire_pump_compliance: 'not_applicable',
      fire_pump_comments: '',
      fire_hydrants_compliance: 'not_applicable',
      fire_hydrants_comments: '',
      fire_suppression_system_compliance: 'not_applicable',
      fire_suppression_system_comments: '',
      fire_safety_signage_compliance: 'not_applicable',
      fire_safety_signage_comments: '',

      emergency_preparedness_compliance: 'not_applicable',
      emergency_preparedness_comments: '',
      evacuation_routes_compliance: 'not_applicable',
      evacuation_routes_comments: '',
      fire_detection_compliance: 'not_applicable',
      fire_detection_comments: '',

      // Emergency Preparedness - Detailed Fields
      emergency_assembly_points_compliance: 'not_applicable',
      emergency_assembly_points_comments: '',
      emergency_contact_list_compliance: 'not_applicable',
      emergency_contact_list_comments: '',
      emergency_procedures_compliance: 'not_applicable',
      emergency_procedures_comments: '',
      first_aid_kits_compliance: 'not_applicable',
      first_aid_kits_comments: '',
      emergency_communication_compliance: 'not_applicable',
      emergency_communication_comments: '',
      emergency_drills_compliance: 'not_applicable',
      emergency_drills_comments: '',
      emergency_exits_compliance: 'not_applicable',
      emergency_exits_comments: '',
      emergency_wardens_compliance: 'not_applicable',
      emergency_wardens_comments: '',
      backup_power_compliance: 'not_applicable',
      backup_power_comments: '',

      electrical_db_compliance: 'not_applicable',
      electrical_db_comments: '',

      // Electrical Safety - Detailed Fields
      electrical_installations_compliance: 'not_applicable',
      electrical_installations_comments: '',
      electrical_testing_compliance: 'not_applicable',
      electrical_testing_comments: '',
      electrical_safety_switches_compliance: 'not_applicable',
      electrical_safety_switches_comments: '',

      hazardous_chemicals_compliance: 'not_applicable',
      hazardous_chemicals_comments: '',

      // Hazardous Materials - Detailed Fields
      chemical_storage_compliance: 'not_applicable',
      chemical_storage_comments: '',
      chemical_handling_procedures_compliance: 'not_applicable',
      chemical_handling_procedures_comments: '',

      article_stacking_compliance: 'not_applicable',
      article_stacking_comments: '',

      // Stacking of Articles - Detailed Field
      stacking_height_limits_compliance: 'not_applicable',
      stacking_height_limits_comments: '',
      housekeeping_compliance: 'not_applicable',
      housekeeping_comments: '',
      floors_compliance: 'not_applicable',
      floors_comments: '',
      walls_compliance: 'not_applicable',
      walls_comments: '',
      ceilings_compliance: 'not_applicable',
      ceilings_comments: '',
      windows_compliance: 'not_applicable',
      windows_comments: '',
      doors_compliance: 'not_applicable',
      doors_comments: '',
      stairways_compliance: 'not_applicable',
      stairways_comments: '',
      lighting_compliance: 'not_applicable',
      lighting_comments: '',

      // Signage Displayed
      signage_displayed_compliance: 'not_applicable',
      signage_displayed_comments: '',

      recommendations: '',
      follow_up_required: false,
      follow_up_date: null
    });
    setCurrentStep(1);
    setStepValidation({ step1: false, step2: false });
    setAssessmentRequirements({
      legal_wall_charts: false,
      signage_displayed: false,
      electrical_db: false,
      fire_fighting_equipment: false,
      fire_detection_systems: false,
      stacking_articles: false,
      automatic_sprinkler: false,
      evacuation_routes: false,
      housekeeping: false,
      emergency_preparedness: false,
      hazardous_chemicals: false,
      general_condition: false
    });
    setUploadedImages([]);
    setFieldImages({}); // Reset all field-specific images
    setSelectedAssessment(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED': return 'bg-blue-100 text-blue-800';
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplianceColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (activeView === 'create' || activeView === 'edit') {
    return (
      <HSEAssessmentForm
        assessmentForm={assessmentForm}
        assessmentRequirements={assessmentRequirements}
        setAssessmentRequirements={setAssessmentRequirements}
        handleFormChange={handleFormChange}
        fieldImages={fieldImages}
        setFieldImages={setFieldImages}
        uploadedImages={uploadedImages}
        setUploadedImages={setUploadedImages}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        nextStep={nextStep}
        prevStep={prevStep}
        validateCurrentStep={validateCurrentStep}
        submitAssessment={submitAssessment}
        isLoading={isLoading}
        error={error}
        success={success}
        properties={properties}
        users={users}
        selectedAssessment={selectedAssessment}
        setActiveView={setActiveView}
        isGeneratingPDF={isGeneratingPDF}
        handleDownloadPDF={handleDownloadPDF}
        user={user}
        resetForm={resetForm}
      />
    );
  }

  if (activeView === 'view' && selectedAssessment) {
    return (
      <HSEAssessmentViewer
        selectedAssessment={selectedAssessment}
        setActiveView={setActiveView}
        onEdit={() => setActiveView('edit')}
        onDelete={deleteAssessment}
        onSubmitForReview={submitAssessmentForReview}
        onApprove={approveAssessment}
        onReject={rejectAssessment}
        getStatusColor={getStatusColor}
        getComplianceColor={getComplianceColor}
        isGeneratingPDF={isGeneratingPDF}
        handleDownloadPDF={handleDownloadPDF}
        user={user}
        handleImageUpload={handleImageUpload}
        handleImageDelete={handleImageDelete}
        properties={properties}
        users={users}
        setAssessmentForm={setAssessmentForm}
        setSelectedAssessment={setSelectedAssessment}
      />
    );
  }

  // Default list view
  return (
    <HSEAssessmentList
      assessments={assessments}
      isLoading={isLoading}
      error={error}
      success={success}
      statistics={statistics}
      filters={filters}
      setFilters={setFilters}
      properties={properties}
      users={users}
      user={user}
      onCreateNew={() => { resetForm(); setActiveView('create'); }}
      onViewAssessment={(assessment) => { setSelectedAssessment(assessment); setActiveView('view'); }}
      onEditAssessment={(assessment) => { setSelectedAssessment(assessment); setActiveView('edit'); }}
      onDeleteAssessment={deleteAssessment}
      onSubmitForReview={submitAssessmentForReview}
      onApproveAssessment={approveAssessment}
      onRejectAssessment={rejectAssessment}
      getStatusColor={getStatusColor}
      getComplianceColor={getComplianceColor}
      BACKEND_URL={BACKEND_URL}
      setSelectedAssessment={setSelectedAssessment}
      setAssessmentForm={setAssessmentForm}
      setUploadedImages={setUploadedImages}
      setFieldImages={setFieldImages}
      setAssessmentRequirements={setAssessmentRequirements}
      setActiveView={setActiveView}
      resetForm={resetForm}
    />
  );
};

export default HSEAssessmentTab;
